import React, { useCallback, useState } from 'react';
import { Img, ImgProps } from 'react-image';

interface ImageProps extends Omit<ImgProps, 'key'> {
  retries?: number;
  retryDelay?: number;
  onError?: () => void;
}

const Image: React.FC<ImageProps> = ({
  retries = 0,
  retryDelay = 1000,
  ...props
}) => {
  const [retryCount, setRetryCount] = useState(0);

  const ImageUnloader: any = useCallback(() => {
    if (retryCount < retries) {
      setTimeout(() => {
        console.warn(
          `Failed to load image, retrying (${retryCount}/${retries})...`,
          props,
        );
        setRetryCount((count) => count + 1);
      }, retryDelay);
    } else {
      console.error(`Failed to load image`, props);
      props.onError?.();
    }
    return props.unloader ?? props.loader;
  }, [retryCount]);

  return (
    <Img
      {...props}
      // Unfortunately, busting via key only doesn't work due to react-image's internal caching
      key={retries ? `${retryCount}/${retries}` : undefined}
      src={
        // If we're not retrying, just use the original src to leverage caching when possible
        !retryCount
          ? props.src
          : typeof props.src === 'string'
            ? bustImageCache(props.src, retryCount)
            : props.src.map((src) => bustImageCache(src, retryCount))
      }
      unloader={<ImageUnloader />}
    />
  );
};

function bustImageCache(src: string, attempt: number) {
  if (!src) return src;

  const url = new URL(src);
  url.searchParams.set('bust', attempt.toString());
  return url.toString();
}

export default Image;
